<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="960"
    max-height="80vh"
    min-height="60vh"
  >
    <template #activator="{ props }">
      <v-btn
        v-bind="props"
        color="secondary"
        dark
        :disabled="(!canCreateDeposit)"
      >
        <v-icon>mdi-upload</v-icon>
        <span title="Add deposit">Add deposit</span>
      </v-btn>
    </template>
    <v-card>
      <v-card-title class="mt-4">
        Add deposit
      </v-card-title>
      <v-row
        justify="center"
        align="center"
        class="row"
      >
        <v-form
          ref="form"
          v-model="valid"
          class="form-narrow"
          @submit.prevent="submitForm"
        >
          <!-- Name -->
          <div class="field w-80">
            <div class="label-row">
              <span
                id="lbl-depositName"
                class="form-label"
              >Name</span>
              <v-tooltip
                text="E.g. 'CUL Records'. This will be the 'Title' field of the package when this Deposit is ingested into the repository."
                content-class="tip-wrap-100"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="info-icon"
                    icon
                    size="x-small"
                    variant="text"
                    :ripple="false"
                    :aria-label="'Help: Name'"
                  >
                    <v-icon>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </div>

            <v-text-field
              id="depositName"
              ref="depositName"
              v-model="depositName"
              :aria-labelledby="'lbl-depositName'"
              placeholder="Enter name"
              :rules="nameRules"
              :validate-on-blur="true"
              :counter="250"
              required
              clearable
              variant="outlined"
              density="comfortable"
            />
          </div>

          <!-- Collecting Area -->
          <div class="field w-80">
            <div class="label-row">
              <span
                id="lbl-colarea"
                class="form-label"
              >Collecting Area</span>
              <v-tooltip
                text="The Collecting Area this Deposit will be assigned to when ingested into the repository."
                content-class="tip-wrap-100"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="info-icon"
                    icon
                    size="x-small"
                    variant="text"
                    :ripple="false"
                    :aria-label="'Help: Collecting Area'"
                  >
                    <v-icon>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </div>

            <v-select
              id="collectingArea"
              ref="collectingArea"
              v-model="collectingArea"
              :aria-labelledby="'lbl-colarea'"
              :items="collectingAreaOptions"
              placeholder="Select collecting area"
              :rules="collectingAreaRules"
              clearable
              variant="outlined"
              density="comfortable"
            />
          </div>

          <!-- Pair 1: Accession Number OR Identifier -->
          <div class="pair-row">
            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-accessionNumber"
                  class="form-label"
                >Accession Number</span>
                <v-tooltip
                  text="E.g. 'GBR/0265/2025/032'. This will become the package's Accession Number on ingest."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: Accession Number'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="accessionNumber"
                ref="accessionNumber"
                v-model="accessionNumber"
                :aria-labelledby="'lbl-accessionNumber'"
                class="pair-left"
                placeholder="Enter accession number"
                :rules="accessionNumberRules"
                clearable
                variant="outlined"
                density="comfortable"
              />
            </div>

            <div
              class="or-chip"
              aria-hidden="true"
            >
              OR
            </div>

            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-identifier"
                  class="form-label"
                >Identifier</span>
                <v-tooltip
                  text="E.g. 'GBR/0012/MS SPR/107'. This will become the package's Identifier on ingest."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: Identifier'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="identifier"
                ref="identifier"
                v-model="identifier"
                :aria-labelledby="'lbl-identifier'"
                class="pair-right"
                placeholder="Enter identifier"
                :rules="identifierRules"
                clearable
                variant="outlined"
                density="comfortable"
              />
            </div>
          </div>

          <!-- Pair 2: ArchivesSpace Accession ID OR ArchivesSpace Archival Object ID -->
          <div class="pair-row">
            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-accessionId"
                  class="form-label"
                >ArchivesSpace Accession ID</span>
                <v-tooltip
                  text="E.g. '27510'. You can find this number at the end of the URL to the accession record in ArchivesSpace, e.g. https://arcspace-staff.lib.cam.ac.uk/accessions/27510. This will be the 'ID of ArchivesSpace Accession Record' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: ArchivesSpace Accession ID'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="accessionId"
                ref="accessionId"
                v-model="accessionId"
                :aria-labelledby="'lbl-accessionId'"
                class="pair-left"
                placeholder="Digits only"
                :rules="accessionIdRules"
                clearable
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                variant="outlined"
                density="comfortable"
              />
            </div>

            <div
              class="or-chip"
              aria-hidden="true"
            >
              OR
            </div>

            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-aaoid"
                  class="form-label"
                >ArchivesSpace Archival Object ID</span>
                <v-tooltip
                  text="E.g. '1728103'. You can find this number at the end of the URL to the record in ArchivesSpace, e.g. https://arcspace-staff.lib.cam.ac.uk/resources/13400#tree::archival_object_1728103. This will be the 'ID of ArchivesSpace Archival Object Record' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: ArchivesSpace Archival Object ID'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="archivesSpaceArchivalObjectRecordId"
                ref="archivesSpaceArchivalObjectRecordId"
                v-model="archivesSpaceArchivalObjectRecordId"
                :aria-labelledby="'lbl-aaoid'"
                class="pair-right"
                placeholder="Enter Archival Object ID"
                :rules="aaoidRules"
                clearable
                variant="outlined"
                density="comfortable"
              />
            </div>
          </div>

          <!-- Actions -->
          <div
            class="w-80"
            style="margin-top: 1rem;"
          >
            <v-btn
              color="error"
              class="mr-4"
              @click="closeDialogue"
            >
              Cancel
            </v-btn>
            <v-btn
              color="secondary"
              class="mr-4"
              :disabled="!valid"
              @click="submitForm"
            >
              Submit
            </v-btn>
          </div>
        </v-form>
      </v-row>
    </v-card>
  </v-dialog>
</template>

<script>
import config from "@/config";
import ShortUniqueId from "short-unique-id";
import { Auth } from '@/utils/amplify-compat';
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { emit } from '@/utils/bus';

const uid = new ShortUniqueId(
  {
    length: 6,
    dictionary: 'alpha'
  }
)

export default {
  props: {
    depositor: {
      type: Object,
      required: true,
    },
  },
  emits: [
    'cancelled',
    'refresh'
  ],
  data () {
    return {
      dialog: false,
      valid: false,
      depositName: null,
      identifier: null,
      accessionNumber: null,
      archivesSpaceArchivalObjectRecordId: null,
      depositKey: null,
      accessionId: null,
      collectingArea: "Department of Archives and Modern Manuscripts",
      collectingAreaOptions: [
        'Cambridge University Digital Library',
        'Cultural Heritage Imaging Laboratory',
        'Department of Archives and Modern Manuscripts',
        'Digital Preservation',
        'Map Department',
        'Music Department',
        'Open Research Systems',
        'World Collections',
      ],
      identities: [],
      errors: [],
      myIdentityId: null,
    }
  },
  computed: {
    nameRules (){
      return [
        v => (v && String(v).trim().length > 0) || 'Name is required',
        v => (!v || v.length <= 250) || 'Name must be less than 250 characters',
      ];
    },
    collectingAreaRules() {
      return [
        v => (v === '' || v === null || v === undefined || this.collectingAreaOptions.includes(v)) || 'Must be one of the listed options',
      ];
    },
    canCreateDeposit() {
      // only allow if the *selected user* has custom:identityid
      return Boolean(this.depositor?.['custom:identityid'])
    },

    accessionNumberRules() {
      return [
        v => (this.isBlank(v) || String(v).length <= 200) || 'Must be ≤ 200 chars',
        () => this.accessionVsIdentifierRule(),   // XOR with identifier
      ];
    },
    identifierRules() {
      return [
        v => (this.isBlank(v) || String(v).length <= 200) || 'Must be ≤ 200 chars',
        () => this.accessionVsIdentifierRule(),   // XOR with accessionNumber
      ];
    },
    accessionIdRules() {
      return [
        v => (this.isBlank(v) || /^\d+$/.test(String(v))) || 'Must be a number',
        () => this.aspaceAccessionVsAOidRule(),   // XOR with AO ID
      ];
    },
    aaoidRules() {
      return [
        v => (this.isBlank(v) || String(v).length <= 200) || 'Must be ≤ 200 chars',
        () => this.aspaceAccessionVsAOidRule(),   // XOR with AS accession
      ];
    },
  },
  watch: {
    accessionId() { this.$refs.form?.validate?.(); },
    archivesSpaceArchivalObjectRecordId() { this.$refs.form?.validate?.(); },
    accessionNumber() { this.$refs.form?.validate?.(); },
    identifier() { this.$refs.form?.validate?.(); },
  },
  async created() {
    // fetch a fallback identity id for “self”
    try {
      const creds = await Auth.currentCredentials()
      this.myIdentityId = creds.identityId
    } catch (e) {
      console.warn('Could not load current credentials:', e)
    }
  },
  methods: {
    closeDialogue () {
      this.dialog = false
      this.depositKey = null
      this.depositName = null
      this.identifier = null
      this.accessionNumber = null
      this.archivesSpaceArchivalObjectRecordId = null
      this.accessionId = null
      this.collectingArea = "Department of Archives and Modern Manuscripts"
      this.$emit('cancelled', true)
    },

    isBlank(v) {
      return v === null || v === undefined || String(v).trim() === '';
    },

    // NEW: exactly-one validation
    xorMessage(aVal, bVal, aLabel, bLabel) {
      const aBlank = this.isBlank(aVal);
      const bBlank = this.isBlank(bVal);
      if (aBlank && bBlank) return `Provide ${aLabel} OR ${bLabel}.`;
      if (!aBlank && !bBlank) return `Provide only one of ${aLabel} OR ${bLabel}, not both.`;
      return ''; // exactly one provided -> ok
    },

    // Pair rule functions
    accessionVsIdentifierRule() {
      return this.xorMessage(
        this.accessionNumber,
        this.identifier,
        'Accession Number',
        'Identifier'
      ) || true;
    },

    aspaceAccessionVsAOidRule() {
      return this.xorMessage(
        this.accessionId,
        this.archivesSpaceArchivalObjectRecordId,
        'AS Accession ID',
        'AS Archival Object ID'
      ) || true;
    },

    async submitForm () {
      const { valid } = await this.$refs.form.validate()
      if (!valid) return

      // Safety net (defense in depth)
      const err1 = this.xorMessage(
        this.accessionNumber, this.identifier,
        'Accession Number', 'Identifier'
      );
      const err2 = this.xorMessage(
        this.accessionId, this.archivesSpaceArchivalObjectRecordId,
        'AS Accession ID', 'AS Archival Object ID'
      );
      if (err1 || err2) {
        this.$refs.form?.validate?.();
        return;
      }

      const ownerIdentityId = this.depositor?.['custom:identityid']
      if (!ownerIdentityId) {
        console.warn('Selected user has no custom:identityid; ask them to sign in once to initialize.')
        // optionally surface a snackbar/toast here for the admin
        return
      }

      this.depositKey = this.generateKey()

      const metadata = this.generateMetadata()
      await this.createDeposit(metadata, ownerIdentityId)
      this.closeDialogue()
    },
    generateKey () {
      return uid.rnd()
    },
    generateMetadata () {
      return {
        "id": this.depositKey,
        "name": this.depositName,
        "depositor": this.depositor,
        "identifier": this.identifier,
        "accessionNumber": this.accessionNumber,
        "accessionId": this.accessionId,
        "archivesSpaceArchivalObjectRecordId": this.archivesSpaceArchivalObjectRecordId,
        "collectingArea": this.collectingArea,
        "createdDate": new Date(),
        "status": "open"
      }
    },
    async createDeposit (fileContentObject, depositorIdentityId) {
      const credentials = await Auth.currentCredentials()
      const client = new S3Client({
        region: config.region,
        credentials: Auth.essentialCredentials(credentials),
        forcePathStyle: true,
      })

      const key = `private/${depositorIdentityId}/deposits/${fileContentObject.id}`

      const params = {
        Bucket: config.bucketName,
        Key: `private/${depositorIdentityId}/deposits/${fileContentObject.id}`,
        Body: JSON.stringify(fileContentObject),
        CacheControl: "no-store, max-age=0, must-revalidate",
        ContentType: "application/json",
      }

      console.log('PUT key:', key)

      try {
        console.log('PUT deposit key:', params.Key)
        const res = await client.send(new PutObjectCommand(params))
        console.log("Successfully put the object:", res)
      } catch (err) {
        console.log("Error:", err)
      } finally {
        emit('refresh', true)
        this.$emit('refresh', true)
      }
    },
  }
}
</script>

<style scoped>
.v-form {
  min-width: 25%;
}

/* External labels + centered info icon */
.label-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin: 0.25rem auto;
  width: 100%;
  text-align: center;
}
.form-label {
  font-weight: 600;
  line-height: 1.2;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}
.info-icon .v-icon {
  opacity: 0.7;
}
.info-icon:hover .v-icon,
.info-icon:focus-visible .v-icon {
  opacity: 1;
}

/* Pair layout + OR chip */
.pair-row {
  display: grid;
  grid-template-columns: minmax(0,1fr) auto minmax(0,1fr);
  gap: 0.75rem;
  align-items: start;
  width: 100%;
  margin: 0.75rem auto 0;
}
.w-80 {
  width: 80%;
  margin-left: auto;
  margin-right: auto;
}
.pair-left, .pair-right { width: 100%; }
.or-chip {
  align-self: center;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1;
  padding: 0.4rem 0.6rem;
  border: 1px solid rgba(0,0,0,0.2);
  border-radius: 999px;
  user-select: none;
  opacity: 0.75;
}

/* Tooltip wrapping (~100 characters) */
:deep(.tip-wrap-100) {
  max-width: 100ch !important;      /* ≈ 100 characters */
  white-space: normal !important;    /* override Vuetify's nowrap */
  overflow-wrap: anywhere;           /* break long tokens/URLs */
  word-break: normal;
  text-align: left;
  line-height: 1.25;
}

/* Small screens */
@media (max-width: 600px) {
  :deep(.tip-wrap-100) { max-width: 90vw !important; }
}

@media (max-width: 800px) {
  .pair-row { grid-template-columns: 1fr; width: 100%; }
  .w-80 { width: 100%; }
  .or-chip { display: none; }
}
</style>
